import { ReactElement } from "react";
import { createPortal } from "react-dom";

import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Guid } from "@/Common/Models/Guid";
import { IConfigureFlowsheetResult, useConfigureFlowsheet } from "@/Flowsheets/Hooks/useConfigureFlowsheet";
import { ConfigureFlowsheetModalStepEnum } from "@/Flowsheets/Models/CareElements/ConfigureFlowsheetModalStepEnum";
import { ConfigurationStep } from "./ConfigurationStep";
import TemplateTypeSelectionStep from "./TemplateTypeSelectionStep";

import styles from "./ConfigureFlowsheetModal.module.scss";

interface IConfigureFlowsheetModalControllerProps {
    /**
     * The current template id
     */
    currentTemplateId: Guid;

    /**
     * The flowsheet id
     */
    flowsheetId: Guid;

    /**
     * The ID of the patient of this flowsheet
     */
    patientId: Guid;

    /**
     * The function to call when closing the modal
     */
    onClose: () => void;

    /**
     * The function to call when applying the saved flowsheet template to the current flowsheet
     * @returns void
     */
    onApplySavedFlowsheetTemplateToCurrentFlowsheet: () => Promise<void>;
}

function Content(result: Readonly<IConfigureFlowsheetResult>): JSX.Element {
    const { getText } = useTranslation();

    switch (result.state.configurationStep) {
        case ConfigureFlowsheetModalStepEnum.Configuration:
            return (
                <ConfigurationStep
                    careComponentsConfig={result.state.careComponentsConfig}
                    onSectionOpenStates={result.actions.setCareComponentSectionExpandedState}
                    onComponentLinkButtonClick={result.actions.setCareComponentActiveState}
                    onRemoveCareElement={result.actions.removeCareElementFromCareComponent}
                    onAddCareElement={result.actions.addCareElementToCareComponent}
                    editCareElementConfig={result.actions.editCareElementConfig}
                    invalidCareElements={result.state.invalidConfigurationCareElements}
                />
            );
        case ConfigureFlowsheetModalStepEnum.TemplateType:
            return (
                <TemplateTypeSelectionStep
                    templateName={result.state.templateName}
                    onTemplateNameChange={result.actions.setTemplateName}
                    onTemplateScopeSelected={result.actions.setTemplateScope}
                    templateScope={result.state.templateScope}
                    isTemplateNameControlDisabled={result.state.isTemplateNameControlDisabled}
                    errorMessage={result.state.isTemplateNameDuplicate ? getText("Flowsheets_TemplateNameAlreadyExists_ErrorDescription") : null}
                />
            );
        default:
            return null;
    }
}

function ConfigureFlowsheetModalController(props: Readonly<IConfigureFlowsheetModalControllerProps>): ReactElement {
    const { getText } = useTranslation();
    const result: IConfigureFlowsheetResult = useConfigureFlowsheet(props.currentTemplateId, props.flowsheetId, props.patientId, props.onClose);

    const isConfigurationStep = result.state.configurationStep === ConfigureFlowsheetModalStepEnum.Configuration;
    return (
        result.state.careComponentsConfig ? (
            createPortal(
                <Modal
                    width={740}
                    height={isConfigurationStep ? 800 : 324}
                    centerContent
                    showOverlay
                    showX
                    scrollableContent
                    title={isConfigurationStep
                        ? getText("Flowsheets_ConfigureFlowsheetLabel")
                        : getText("Flowsheets_SaveFlowsheetLabel")
                    }
                    footer={(
                        <div className={styles.configureFlowsheetModalFooter}>
                            {result.state.configurationStep === ConfigureFlowsheetModalStepEnum.TemplateType && (
                                <CheckboxControl
                                    label={getText("Flowsheets_ApplyChangesToCurrentFlowsheetLabel")}
                                    checked={result.state.isApplyChangesChecked}
                                    onChange={result.actions.toggleIsApplyChangesChecked}
                                />
                            )}
                            <ButtonGroup spacing="sm">
                                <ButtonControl
                                    variant="neutral"
                                    onClick={() => result.actions.onCancel()}
                                    label={getText("CancelLabel")}
                                />
                                <ButtonControl
                                    dataCy="flowsheets-configuration-save-button"
                                    variant="brand"
                                    disabled={result.state.isSavingDisabled}
                                    onClick={() => result.actions.onSave({
                                        onApplySavedFlowsheetTemplateToCurrentFlowsheet: props.onApplySavedFlowsheetTemplateToCurrentFlowsheet
                                    })}
                                    label={getText("SaveLabel")}
                                />
                            </ButtonGroup>
                        </div>
                    )}
                    onClose={() => props.onClose()}
                >
                    <Content {...result} />
                </Modal>,
                document.body
            )
        ) : null);
}

export { ConfigureFlowsheetModalController };
